/**
 * MCP plugin-namespaced name parsing — single source of truth for the SDK-name
 * ↔ bare-name identity drift that split the Settings MCP panel into two rows
 * per plugin server (one always "未连接", one orphaned as "独立/运行时").
 *
 * Root cause: the Claude Agent SDK reports LIVE MCP server status for a
 * plugin's `.mcp.json` entries under its own namespaced key
 * `plugin:<pluginName>:<serverName>` (e.g. `plugin:catalyst-juguang:catalyst-juguang`),
 * while DOSIA's plugin registry / static catalog rows key the SAME server by
 * its bare `serverName` (`catalyst-juguang`, from `plugin.mcpServerNames`).
 * Anything that merges "declared" rows with "live" rows by exact name match
 * therefore sees two different identities for one server.
 *
 * Three-repo alignment: claude-code's own plugin manager UI
 * (`src/commands/plugin/ManagePlugins.tsx`) parses this same `plugin:x:y`
 * form by `:` split — `parts[1]` is the plugin name, `parts.slice(2).join(":")`
 * is the server name (server names may themselves contain `:`) — and groups
 * live clients under their owning plugin while displaying the server name.
 * This module mirrors that split so DOSIA's merge/reverse-lookup consumers
 * agree with the SDK's own convention instead of hand-rolling a second parser.
 *
 * The `plugin_<plugin>_<server>` UNDERSCORE form is the SDK's *tool name*
 * namespacing (`mcp__plugin_<p>_<s>__<tool>`) — a different convention for a
 * different surface, and it lives in this module too (see
 * `pluginNamespacedMcpServerName` / `normalizeMcpServerNameToBare` below) so
 * both spellings of the same identity have ONE definition (红线 #9). It used to
 * be hand-written as a template literal in two places (cloud-mcp-materializer.ts
 * and registry.ts), which is exactly the drift that let a *third* consumer
 * (agent-session's allowlist filter) disagree about whether a name matched.
 *
 * WHY normalization matters (2026-07-29 生产实证, jianghongyi「聚光时报」cron):
 * the ONLY spelling a model ever SEES is the tool-name one — its tools are
 * called `mcp__plugin_catalyst-juguang_catalyst-juguang__*`, so when a tool asks
 * it for "the MCP connector name" it reasonably answers
 * `plugin_catalyst-juguang_catalyst-juguang`. The `.mcp.json` key (and therefore
 * every DOSIA allowlist consumer) is the BARE `catalyst-juguang`. Two consumers
 * accepted both spellings, one accepted only the bare form and dropped the rest
 * SILENTLY → the connector materialized fine yet reached the session with zero
 * tools, twice, with no error anywhere. Normalizing at the entry points is the
 * single-root fix; per-consumer leniency is not.
 */
/**
 * The one sentence every model-facing `mcpServers` field must carry (红线 #9 单一
 * 真相源 — `cron_manage` execution.mcpServers / `cloud_task_manage` / `create_agent`
 * all embed THIS string, they don't each phrase it).
 *
 * WHY it has to be spelled out (2026-07-29 生产实证): the field used to say only
 * "the SUBSET of MCP connector names to mount", and the only example given was
 * `['feishu']` — an in-process `{type:"sdk",instance}` server that bypasses
 * allowlist filtering entirely, so that example could never expose a wrong
 * spelling. Meanwhile the ONLY spelling the model can observe is the tool name
 * `mcp__plugin_catalyst-juguang_catalyst-juguang__*`, from which
 * `plugin_catalyst-juguang_catalyst-juguang` is the natural (and wrong) guess.
 * Assembly now normalizes both spellings, but the description is what stops the
 * model from guessing in the first place — and normalization can only rescue
 * connectors this machine has actually discovered.
 */
export const MCP_SERVER_NAME_SPELLING_RULE = "NAME FORMAT: use the BARE connector name as declared in the plugin's `.mcp.json` " +
    "(e.g. 'catalyst-juguang'), NOT the `plugin_<plugin>_<connector>` prefix form you " +
    "see inside tool names — in a tool called `mcp__plugin_X_Y__some_tool` the " +
    "connector name is `Y`, not `plugin_X_Y`.";
/**
 * The EXPLICIT "mount everything this workspace has" marker for any
 * connector-narrowing axis (`CronExecution.agentTurn.mcpServers`,
 * `AgentHostTurnRequest.mcpServers`). SINGLE definition (红线 #9) — no consumer
 * hand-writes the literal.
 *
 * WHY a sentinel and not "just omit the field" (2026-07-29 生产实证, jianghongyi
 * 「聚光时报」cron): 遗漏 (`undefined`) 与空 (`[]`) 不是一回事。SERVER 路径
 * `assertServerAgentTurnExecution` 拒绝 `undefined`，避免模型忘写工具面就静默继承。
 * 作者要「真的全挂」必须写 {@link ALL_MCP_SERVERS}。REQ-91 之后桌面裸 `undefined`
 * 也是默认最小面（不再等于满配），所以桌面 fold 与云端 host 收敛到同一条实现：
 * 把 `"all"` 展开成本机已发现的连接器名单，走 allowlist + 物化（一律
 * `strictMcpConfig:true`）。不再靠「不钉 strict 让 SDK 自己扫 .mcp.json」。
 *
 * Shape choice: a `string[] | "all"` UNION (not a `["*"]` sentinel INSIDE the
 * array) mirroring the existing `WorkspaceConfig.managedSkills?: string[] | "all"`
 * precedent (workspace-config.ts:471-472). The union is what makes the compiler
 * walk every consumption point: a sentinel hidden inside `string[]` would keep
 * `req.mcpServers.includes("feishu-cloud")` / `.length > 0` / the allowlist filter
 * compiling while silently meaning the wrong thing — which is the exact
 * silent-drift class this whole change exists to kill (红线 #9 必标注共享类型逼 TS 兜底).
 */
export const ALL_MCP_SERVERS = "all";
/** Narrowing guard for the 显式全量 marker. Use this instead of `=== "all"`. */
export function isAllMcpServers(scope) {
    return scope === ALL_MCP_SERVERS;
}
/**
 * Parse a live MCP server status name of the form `plugin:<pluginName>:<serverName>`.
 * Returns `null` when the name doesn't carry the `plugin:` namespace prefix, or
 * when it does but doesn't decompose into a non-empty plugin name + non-empty
 * server name (malformed / unexpected SDK shape — degrade to "not a plugin
 * name" rather than guessing).
 *
 * `serverName` may itself contain `:` (rejoined via `parts.slice(2).join(":")`,
 * mirroring claude-code's own parser) — only the FIRST two colons are
 * structural (`plugin:` literal + pluginName); everything after is serverName.
 */
export function parsePluginMcpName(name) {
    if (!name.startsWith("plugin:"))
        return null;
    const parts = name.split(":");
    if (parts.length < 3)
        return null;
    const pluginName = parts[1];
    const serverName = parts.slice(2).join(":");
    if (!pluginName || !serverName)
        return null;
    return { pluginName, serverName };
}
/** Prefix of the SDK *tool-name* namespaced spelling (UNDERSCORE form). */
const PLUGIN_TOOL_NAME_PREFIX = "plugin_";
/**
 * The SDK *tool-name* namespaced spelling of a plugin `.mcp.json` connector:
 * `plugin_<pluginName>_<serverName>`, i.e. the `<server>` segment the model sees
 * inside `mcp__plugin_<p>_<s>__<tool>`. SINGLE definition of this template —
 * every producer (materializer allowlist match, registry target scan) and the
 * normalizer below derive from here (红线 #9).
 */
export function pluginNamespacedMcpServerName(pluginName, serverName) {
    return `${PLUGIN_TOOL_NAME_PREFIX}${pluginName}_${serverName}`;
}
/**
 * Normalize ONE MCP-connector name to the bare `.mcp.json` key that every DOSIA
 * allowlist consumer matches on, given the set of bare names actually known on
 * this machine (`plugin.mcpServerNames`).
 *
 * Accepts, in order:
 *   1. an already-bare known name → returned unchanged;
 *   2. the tool-name spelling `plugin_<plugin>_<server>` (what a model copies out
 *      of `mcp__plugin_<p>_<s>__<tool>`) → `<server>`;
 *   3. the live-status spelling `plugin:<plugin>:<server>` (what a user copies out
 *      of the Settings MCP panel) → `<server>`.
 *
 * Anything not recognizable against `knownBareNames` is returned UNCHANGED — this
 * is a normalizer, not a validator: an unknown name must stay intact so the
 * caller's own reconcile/warn path can report it verbatim to the model.
 *
 * The underscore form is genuinely ambiguous on its own (both plugin names and
 * server names may contain `_`), which is why the decision is made against the
 * known set rather than by splitting: we only ever map onto a name that already
 * exists. When several known names could match, the LONGEST wins (the most
 * specific suffix is the one the SDK would have produced).
 */
export function normalizeMcpServerNameToBare(name, knownBareNames) {
    const known = knownBareNames instanceof Set ? knownBareNames : new Set(knownBareNames);
    if (known.has(name))
        return name;
    // Live-status colon form — structurally unambiguous, reuse the existing parser.
    const colon = parsePluginMcpName(name);
    if (colon && known.has(colon.serverName))
        return colon.serverName;
    if (!name.startsWith(PLUGIN_TOOL_NAME_PREFIX))
        return name;
    let best;
    for (const bare of known) {
        // `plugin_<plugin>_<bare>` — require a non-empty plugin segment between the
        // prefix and the trailing `_<bare>`, so `plugin__foo` never resolves to `foo`.
        const suffix = `_${bare}`;
        if (!name.endsWith(suffix))
            continue;
        if (name.length <= PLUGIN_TOOL_NAME_PREFIX.length + suffix.length)
            continue;
        if (!best || bare.length > best.length)
            best = bare;
    }
    return best ?? name;
}
export function normalizeMcpServerAllowlist(allowlist, knownBareNames) {
    if (allowlist === undefined)
        return undefined;
    if (isAllMcpServers(allowlist))
        return allowlist;
    if (allowlist.length === 0)
        return allowlist;
    const known = knownBareNames instanceof Set ? knownBareNames : new Set(knownBareNames);
    return [...new Set(allowlist.map((n) => normalizeMcpServerNameToBare(n, known)))];
}
//# sourceMappingURL=mcp-plugin-name.js.map